import { Injectable } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly defaultDuration = 3000;
  private readonly defaultPosition: ToastOptions['position'] = 'bottom-right';

  /**
   * Exibe uma notificação toast
   * @param message Mensagem a ser exibida
   * @param type Tipo do toast (success, error, info, warning)
   * @param options Opções adicionais (duração, posição)
   */
  public show(
    message: string,
    type: ToastType = 'info',
    options: ToastOptions = {},
  ): void {
    // Verificar se estamos no browser
    if (typeof document === 'undefined') {
      console.warn('ToastService: document não está disponível');
      return;
    }

    const duration = options.duration ?? this.defaultDuration;
    const position = options.position ?? this.defaultPosition;

    // Ícone baseado no tipo
    const icon = this.getIcon(type);

    // Criar elemento do toast
    const toast = document.createElement('div');
    toast.className = this.getToastClasses();
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');

    // Aplicar estilos inline para garantir cores sólidas (sem transparência)
    const styles = this.getToastStyles(type);
    Object.assign(toast.style, styles);
    toast.style.padding = '0.5rem';
    toast.style.marginBottom = '0.5rem 0.8rem';
    toast.style.opacity = '1'; // Garantir que não há transparência no elemento
    toast.style.backgroundColor = styles['backgroundColor'] || ''; // Forçar background sólido
    toast.style.borderColor = styles['borderColor'] || ''; // Forçar borda sólida

    // Montar estrutura
    toast.innerHTML = `
      <div class="flex items-start gap-2">
           <span class="text-xs text-gray-900 dark:text-white">
             ${icon}
           </span>
           <p class="text-sm text-gray-900 dark:text-white">
             ${this.escapeHtml(message)}
           </p>
      </div>
    `;

    // Adicionar evento de fechar
    const closeBtn = toast.querySelector('button');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.removeToast(toast));
    }

    // Adicionar ao DOM
    const container = this.getOrCreateContainer(position);
    container.appendChild(toast);

    // Forçar reflow para garantir que as classes sejam aplicadas
    void toast.offsetHeight;

    // Animar entrada após um pequeno delay para garantir que o elemento está no DOM
    setTimeout(() => {
      toast.classList.remove('opacity-0', 'translate-y-2');
      toast.classList.add('opacity-100', 'translate-y-0');
      // Garantir que o opacity seja 1 explicitamente no estilo inline
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    }, 10);

    // Remover automaticamente após duração
    if (duration > 0) {
      setTimeout(() => {
        this.removeToast(toast);
      }, duration);
    }
  }

  /**
   * Escapa HTML para prevenir XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Exibe toast de sucesso
   */
  public success(message: string, options?: ToastOptions): void {
    this.show(message, 'success', options);
  }

  /**
   * Exibe toast de erro
   */
  public error(message: string, options?: ToastOptions): void {
    this.show(message, 'error', options);
  }

  /**
   * Exibe toast de informação
   */
  public info(message: string, options?: ToastOptions): void {
    this.show(message, 'info', options);
  }

  /**
   * Exibe toast de aviso
   */
  public warning(message: string, options?: ToastOptions): void {
    this.show(message, 'warning', options);
  }

  /**
   * Obtém classes CSS do toast baseado no tipo e posição
   */
  private getToastClasses(): string {
    const baseClasses =
      'max-w-sm w-full shadow-lg rounded-md pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden transition-all duration-300 ease-in-out opacity-0 translate-y-2';
    return baseClasses;
  }

  /**
   * Obtém estilos inline baseado no tipo
   */
  private getToastStyles(type: ToastType): { [key: string]: string } {
    const isDarkMode =
      document.documentElement.classList.contains('dark') ||
      document.documentElement.getAttribute('data-hs-theme') === 'dark';

    if (isDarkMode) {
      const darkStyles = {
        success: { backgroundColor: '#14532d', borderColor: '#166534' },
        error: { backgroundColor: '#7f1d1d', borderColor: '#991b1b' },
        info: { backgroundColor: '#1e3a8a', borderColor: '#1e40af' },
        warning: { backgroundColor: '#78350f', borderColor: '#92400e' },
      };
      return { ...darkStyles[type], borderWidth: '1px', borderStyle: 'solid' };
    } else {
      const lightStyles = {
        success: { backgroundColor: '#dcfce7', borderColor: '#86efac' },
        error: { backgroundColor: '#fee2e2', borderColor: '#fca5a5' },
        info: { backgroundColor: '#dbeafe', borderColor: '#93c5fd' },
        warning: { backgroundColor: '#fef3c7', borderColor: '#fde047' },
      };
      return { ...lightStyles[type], borderWidth: '1px', borderStyle: 'solid' };
    }
  }

  /**
   * Obtém ícone baseado no tipo
   */
  private getIcon(type: ToastType): string {
    const icons = {
      success: '<i class="bi bi-check-circle-fill text-green-400 text-sm"></i>',
      error: '<i class="bi bi-x-circle-fill text-red-400 text-sm"></i>',
      info: '<i class="bi bi-info-circle-fill text-blue-400 text-sm"></i>',
      warning:
        '<i class="bi bi-exclamation-triangle-fill text-yellow-400 text-sm"></i>',
    };
    return icons[type];
  }

  /**
   * Obtém ou cria container de toasts
   */
  private getOrCreateContainer(
    position: ToastOptions['position'],
  ): HTMLElement {
    const containerId = `toast-container-${position}`;
    let container = document.getElementById(containerId);

    if (!container) {
      container = document.createElement('div');
      container.id = containerId;
      container.style.position = 'fixed';
      container.style.zIndex = '9999';
      container.style.pointerEvents = 'none';

      // Aplicar estilos baseado na posição
      switch (position) {
        case 'top-right':
          container.style.top = '1rem';
          container.style.right = '1rem';
          break;
        case 'top-left':
          container.style.top = '1rem';
          container.style.left = '1rem';
          break;
        case 'bottom-right':
          container.style.bottom = '1rem';
          container.style.right = '1rem';
          break;
        case 'bottom-left':
          container.style.bottom = '1rem';
          container.style.left = '1rem';
          break;
        default:
          container.style.top = '1rem';
          container.style.right = '1rem';
      }

      document.body.appendChild(container);
    }

    return container;
  }

  /**
   * Remove toast do DOM
   */
  private removeToast(toast: HTMLElement): void {
    toast.classList.remove('opacity-100', 'translate-y-0');
    toast.classList.add('opacity-0', 'translate-y-2');

    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }
}
